/**
 * OASIS application display model — the shared, serializable view of a
 * submitted application body.
 *
 * WHY THIS EXISTS
 *
 * Two screens render the same application body: the applicant's read-only view
 * (`projects/oasis`) and the internal OARM detail accordion (`src/app`). Both
 * used to dump `JSON.stringify(body[sectionKey])` into a `<pre>`, which meant
 * every reader — applicant and reviewer alike — was shown raw enum codes and
 * camelCase keys.
 *
 * The obvious fix (a formatted component per section) cannot live in this
 * library on its own, because the display VOCABULARY it needs was trapped in
 * the applicant form's templates and unreachable from the internal app. So the
 * split is deliberate:
 *
 *  - This library owns the PROJECTION: application body → display model. Pure
 *    TypeScript, no Angular, no ng-bootstrap, no store, no HTTP. It resolves
 *    every enum to a label and every derived value exactly once, for both apps.
 *  - Each host app owns the CHROME: the accordion, the cards, the print view.
 *    A host walks `DisplayBlock[]` and renders it however it likes.
 *
 * Keeping the projection pure is what makes it testable without a DOM, which
 * matters because the mapping is where the bugs are, not the markup.
 *
 * DATES ARE CARRIED AND RENDERED RAW, DELIBERATELY. Every date-valued field emits
 * its original ISO `YYYY-MM-DD` string, and `formatIsoDate` shows it unchanged.
 * The body stores date-only values, and constructing a `Date` from `'2026-05-15'`
 * parses it as UTC midnight — which renders as May 14 in every US timezone.
 * Rather than carry a bespoke timezone-safe formatter forever, the display layer
 * simply does not convert: ISO is unambiguous, fixed-width, and sorts correctly,
 * which suits a reviewer scanning dates. Do not hand a date-only value to
 * Angular's `date` pipe. Real instants — `uploadedAtIso` on a file reference, or
 * workflow event timestamps — are a different thing and are safe to format.
 */

/**
 * Who is reading. This drives more than wording: reviewers must never receive
 * the demographics block, and applicants must never see OARM review flags.
 */
export type DisplayAudience = 'applicant' | 'staff';

/** Why a field or section has nothing to show. */
export type DisplayAbsenceReason =
	/** The question was in scope and went unanswered. */
	| 'not_provided'
	/**
	 * The question did not apply, given an earlier answer — e.g. the dual
	 * citizenship country when the applicant is not a dual citizen. Distinct
	 * from `not_provided`: nothing is missing here.
	 */
	| 'not_applicable';

/** A reference to an uploaded file. The bytes live in S3; this is the pointer. */
export interface DisplayFileRef {
	s3Key: string;
	filename: string;
	/** Resolved label for the document category, not the raw `OasisDocType`. */
	docTypeLabel: string;
	sizeBytes: number;
	/** A real instant, unlike the date-only fields — safe for the `date` pipe. */
	uploadedAtIso: string;
	description?: string;
}

/**
 * A failed attempt to open one uploaded file, keyed to the file it belongs to so
 * the message can appear on that row. A section can list several documents.
 */
export interface DisplayFileDownloadError {
	s3Key: string;
	/** Shown as-is. The host owns the wording, because it owns the transport. */
	message: string;
}

/**
 * A single rendered value. The union exists so hosts can style by meaning
 * (a long essay is not a one-line answer, a file is not a string) without the
 * projection having to know anything about markup.
 */
export type DisplayValue =
	/** A short, single-line answer. Already label-resolved if it came from an enum. */
	| { kind: 'text'; value: string }
	/** Free text that may contain paragraphs — essays, duties, explanations. */
	| { kind: 'longText'; value: string }
	/** Raw ISO `YYYY-MM-DD`. See the file header on why this is not pre-formatted. */
	| { kind: 'date'; iso: string }
	/** A span. `toIso` is empty when the entry is open-ended. */
	| { kind: 'dateRange'; fromIso: string; toIso: string; ongoing?: boolean }
	/** Several short values — jurisdictions, majors, selected categories. */
	| { kind: 'list'; items: string[] }
	| { kind: 'link'; href: string; text: string }
	| { kind: 'file'; file: DisplayFileRef }
	| { kind: 'empty'; reason: DisplayAbsenceReason };

export interface DisplayField {
	label: string;
	value: DisplayValue;
	/** Secondary explanation, e.g. the definition behind a demographic category. */
	hint?: string;
}

/**
 * One repeating record — an employment row, a timeline entry, a reference.
 * `title` and `subtitle` let a host render a recognizable card header without
 * having to guess which of the fields is the identifying one.
 */
export interface DisplayEntry {
	title: string;
	subtitle?: string;
	/** Short status-like tags, e.g. 'Part-time', 'Derived from clerkship'. */
	badges?: string[];
	fields: DisplayField[];
}

/** A checked item from a check-all-that-apply list, with its optional detail. */
export interface DisplayChecklistItem {
	label: string;
	description?: string;
}

/**
 * A renderable chunk of a section.
 *
 * `checklist` carries only the SELECTED items plus `totalAvailable`. Professional
 * Accomplishments is 42 yes/no items and Honors & Awards is 15; rendering the
 * unselected ones buries the answer in noise. The count is kept so the reader
 * still knows the denominator — "6 of 42" is information, and without it a short
 * list is indistinguishable from a truncated one.
 */
export type DisplayBlock =
	| { kind: 'fields'; heading?: string; fields: DisplayField[] }
	| { kind: 'entries'; heading?: string; entries: DisplayEntry[]; emptyText: string }
	| { kind: 'checklist'; heading?: string; selected: DisplayChecklistItem[]; totalAvailable: number }
	| { kind: 'files'; heading?: string; files: DisplayFileRef[]; emptyText: string }
	/** Standalone prose — a gap explanation, an applicant note. */
	| { kind: 'note'; heading?: string; text: string };

/**
 * Whether a section has anything behind it.
 *
 * `withheld` is NOT the same as `not_provided`, and conflating them makes the UI
 * state a falsehood: telling a reviewer that demographics were "not provided"
 * when the API stripped them implies the applicant declined to self-identify.
 * The projection only emits `withheld` when it is told the data was removed.
 *
 * A section the audience must never see at all does not get a state — it is
 * absent from `ApplicationDisplayModel.sections` entirely.
 */
export type DisplaySectionState = 'answered' | 'not_provided' | 'withheld';

export interface DisplaySection {
	/** The `IOasisApplicationBase` field name. See `oasis-section-registry.ts`. */
	key: string;
	label: string;
	state: DisplaySectionState;
	/** Empty for every state except `answered`. */
	blocks: DisplayBlock[];
}

export interface ApplicationDisplayModel {
	sections: DisplaySection[];
}

/**
 * Whether a host should give this section an expand/collapse affordance.
 *
 * A collapsible header that opens to reveal "Not provided" — when the header
 * already says "Not provided" — is an interaction that cannot pay off. Hosts
 * render non-expandable sections as a static heading plus a badge rather than a
 * disabled button, because a `disabled` button leaves the tab order and a
 * keyboard or screen-reader user can no longer reach it to find out why it is
 * inert.
 */
export function isSectionExpandable(section: DisplaySection): boolean {
	return section.state === 'answered' && section.blocks.length > 0;
}
